import React, { useState } from "react";
import { Link } from "react-router-dom";
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import Axis_Logo from '../images/Axis_image6.png';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import JoinModal from '../components/JoinModal';
import "../styles/home.css";
import "../styles/join.css";

export default function Join() {
  useScrollBehavior();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <main className="home">
      <PageSEO
        title="Join | The Axis"
        description="Become a member of the Axis — a community of people committed to the Sacred Three and prepared to act on them."
        path="/join"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Join The Axis",
          "url": (process.env.REACT_APP_SITE_URL || "") + "/join"
        }}
      />

      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">

        {/* ── HERO ── */}
        <div className="module join-hero__module">
          <div className="home__logo">
            <img src={Axis_Logo} alt="Axis logo" className="axis_logo" />
          </div>
          <span className="about__eyebrow">The Axis</span>
          <h1 className="home__title">Join the Organization</h1>
          <p className="home__subtitle">This is not a newsletter signup.</p>
          <p className="home__copy">
            Membership in the Axis is an acceptance of obligations — to the Sacred
            Three, to the community, and to yourself. It is for people who have
            decided that knowing what is sacred obligates them to act accordingly.
          </p>
          <div className="home__cta" style={{ marginTop: '1.5rem' }}>
            <button className="btn btn--primary join-hero__cta" onClick={() => setModalOpen(true)}>
              Join the Axis
            </button>
            <Link className="btn btn--ghost" to="/codex">Read the Codex First</Link>
          </div>
        </div>

        {/* ── WHAT MEMBERSHIP MEANS ── */}
        <div className="module join-what__module">
          <h2 className="module__heading">What Membership Means</h2>
          <p className="module__subheading">
            Three things — what you accept, what you commit to, and what you
            become part of.
          </p>
          <div className="features__grid">

            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4l3.09 6.26L26 11.27l-5 4.87 1.18 6.88L16 19.77l-6.18 3.25L11 16.14 6 11.27l6.91-1.01L16 4z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4 className="feature__title">You Accept the Sacred Three</h4>
              <p className="feature__body">
                Life, the Planet, and the Social Contract are not negotiable
                positions for you. They are the foundation of your decisions and
                actions — inside the organization and outside it.
              </p>
            </div>

            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="10" r="5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 26c0-5.523 4.477-8 10-8s10 2.477 10 8"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h4 className="feature__title">You Commit to the Practice</h4>
              <p className="feature__body">
                Daily physical practice. Ongoing study. Community participation.
                Regular contribution to Axis charitable and community actions.
                Membership is active, not passive.
              </p>
            </div>

            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="22" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 24c0-4 3.582-6 8-6M22 18c4.418 0 8 2 8 6"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M13 24c0-3 1.343-5 3-5s3 2 3 5"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h4 className="feature__title">You Join the Community</h4>
              <p className="feature__body">
                Access to the platform, the Codex, and connection to local
                chapters. You become part of an organized, principled community
                working toward a shared purpose.
              </p>
            </div>

          </div>
        </div>

        {/* ── OBLIGATIONS ── */}
        <div className="module join-obligations__module">
          <div className="panel">
            <div className="panel__title">Membership Obligations</div>
            <p className="module__subheading" style={{ marginBottom: '0' }}>
              The Axis does not have passive members. If you join, you participate.
            </p>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">Daily Practice</div>
                <div className="panel__value">
                  Physical training, awareness practice, and cleanliness of body,
                  mind, and environment — maintained daily. The three acts are the
                  foundation of the practitioner.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Study</div>
                <div className="panel__value">
                  Ongoing engagement with the Codex and with the broader
                  philosophical, historical, and practical knowledge the Axis
                  practitioner requires. Ignorance is not a neutral state.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Community Action</div>
                <div className="panel__value">
                  Regular participation in charitable actions, community service,
                  and Axis-organized events. Frequency and form are determined
                  at the chapter level.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Accountability</div>
                <div className="panel__value">
                  You hold yourself to the standard of the Sacred Three and accept
                  accountability to your fellow members. Membership is maintained
                  through demonstrated commitment — not dues alone.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Dues</div>
                <div className="panel__value">
                  A nominal financial contribution to sustain the organization,
                  fund charitable actions, and maintain the platform and resources
                  available to members. Monthly and annual options available.
                  Hardship provisions exist — contact us if needed.
                </div>
              </div>
            </div>
          </div>
        </div>

               {/* ── MEMBERSHIP PLAN ── */}
        <div className="module join-plan__module">
        <h2 className="module__heading">Membership</h2>
        <p className="module__subheading">
            One membership. Everything included. Dues kept nominal —
            the Axis is not a business.
        </p>
        <div className="join-plan__card">
        <div className="join-plan__badge">Full Membership</div>
        <div className="join-plan__pricing">
        <div className="join-plan__price-option">
        <span className="join-plan__price">TBD</span>
        <span className="join-plan__period">/ month</span>
        </div>
        <div className="join-plan__divider">or</div>
        <div className="join-plan__price-option">
        <span className="join-plan__price">TBD</span>
        <span className="join-plan__period">/ year</span>
        <span className="join-plan__save">Best value</span>
        </div>
        </div>
        <ul className="join-plan__includes">
          <li>Full Axis membership and standing</li>
          <li>Access to the platform and all features</li>
          <li>Full Codex access and updates</li>
          <li>Local chapter connection</li>
          <li>Participation in Axis charitable and community actions</li>
          <li>Voting rights in organizational decisions</li>
          <li>Direct access to the Axis community</li>
        </ul>
        <button
            className="btn btn--primary join-plan__cta"
            onClick={() => setModalOpen(true)}
        >
              Join the Axis
        </button>
        <p className="join-plan__note">
              Membership is currently in pre-launch. Submitting interest now
              secures your place and ensures you are contacted when dues processing
              goes live.
        </p>
        <p className="join-plan__note">
              The Axis will never sell your information or share it with third
              parties except as required to process your membership or as
              required by law.
        </p>
        </div>
      </div>

        {/* ── CLOSING ── */}
        <div className="module join-closing__module">
          <div className="panel panel--closing">
            <div className="panel__title">Not Ready to Join Yet?</div>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">Read First</div>
                <div className="panel__value">
                  The Codex is free and complete. Start there.
                  If it is what you have been looking for, you will know.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Ask a Question</div>
                <div className="panel__value">
                  Contact us directly. Membership is a serious commitment
                  and questions deserve real answers.
                </div>
              </div>
            </div>
            <div className="panel__cta">
              <Link className="btn btn--ghost" to="/codex">Read the Codex</Link>
              <Link className="btn btn--ghost" to="/contact">Contact Us</Link>
            </div>
          </div>
        </div>

      </section>

      <JoinModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}